import { useAuthStore } from '@/stores/authStore';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PageSEO } from '@/components/SEO';

// Layout
import { Navbar } from '@/components/layout/Navbar';

// Landing effects
import { CustomCursor } from '@/components/landing/effects/CustomCursor';
import { GrainOverlay } from '@/components/landing/effects/GrainOverlay';
import { ScrollProgress } from '@/components/landing/effects/ScrollProgress';

// Landing hooks
import { useLenis } from '@/components/landing/hooks/useLenis';

// Landing sections
import { HeroSection } from '@/components/landing/HeroSection';
import { ProblemSection } from '@/components/landing/ProblemSection';
import { PsychologyEngineSection } from '@/components/landing/PsychologyEngineSection';
import { HowItWorksSection } from '@/components/landing/HowItWorksSection';
import { FeatureShowcaseSection } from '@/components/landing/FeatureShowcaseSection';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { SocialProofSection } from '@/components/landing/SocialProofSection';
import { PricingSection } from '@/components/landing/PricingSection';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { FooterSection } from '@/components/landing/FooterSection';

gsap.registerPlugin(ScrollTrigger);

// PublicRoute owns the auth redirect for both routes that render this page, so an
// authenticated user is sent to the app before Landing mounts. It stays the only
// place that decides, which is what lets DEV_MODE park a logged-in user here.
export default function Landing() {
  const { isAuthenticated } = useAuthStore();

  // Initialize Lenis smooth scroll (desktop only, respects reduced motion)
  useLenis();

  return (
    <>
      {/* Google Fonts */}
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <CustomCursor />
      <ScrollProgress />
      <GrainOverlay />

      <div className="min-h-screen bg-landing-bg text-[#E8E6F0]">
        <PageSEO.Landing />
        <Navbar
          showAuthButtons={!isAuthenticated}
          isAuthenticated={isAuthenticated}
          theme="dark"
        />
        <main>
          <HeroSection />
          <ProblemSection />
          <PsychologyEngineSection />
          <HowItWorksSection />
          <FeatureShowcaseSection />
          <FeaturesSection />
          <SocialProofSection />
          <PricingSection />
          <FinalCTASection />
        </main>
        <FooterSection />
      </div>
    </>
  );
}
